import {Evaluator} from "@cm/graph-v3"
import {registerImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/register-image-op-node"
import {ImageOpPerPixelFn} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/node"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {createImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc-utils"
import {channelDefinition, definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-channel/definition"
import {z} from "zod/v4-mini"
import {HalCompositorInfo} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/hal"
import {ImageOpParametersOfType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {ImageOpInputsOrNodes} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"

const nodeDefinition = {...definition, nodeClass: "ImageOpNodeExtractChannel"}

@registerImageOpNode
export class ImageOpNodeExtractChannel extends ImageOpPerPixelFn<typeof definition> {
    override get definition() {
        return nodeDefinition
    }

    constructor(parameters: ImageOpInputsOrNodes<typeof definition>) {
        super(parameters)
        this.listenToParameterChange("channel").subscribe(() => this.requestCompositorRebuild())
    }

    protected getCompositingInfo(): HalCompositorInfo {
        return {
            compositingFunction: `
                float value = fetchSourceChannel(0, ${this.getChannelIndex(this.parameters.channel)});
                return vec4(value);
                `,
            sourceParameterNames: ["sourceImage"],
            outDescriptor: {
                channels: 1,
            },
        }
    }

    override toImgProc = async (evaluator: Evaluator<ImageOpContextImgProc>, parameters: ImageOpParametersOfType<typeof definition, "imgproc">) => {
        const evaluatedParameters = await evaluator.getAll(parameters)
        return createImageOpImgProc(
            {
                ...evaluatedParameters.sourceImage.descriptor,
                channels: 1,
            },
            {
                type: "extractChannel",
                input: evaluatedParameters.sourceImage.node,
                channel: this.getChannelIndex(evaluatedParameters.channel),
            },
        )
    }

    private getChannelIndex(channel: z.infer<typeof channelDefinition>) {
        switch (channel) {
            case "R":
            case 0:
                return 0
            case "G":
            case 1:
                return 1
            case "B":
            case 2:
                return 2
            case "A":
            case 3:
                return 3
            default:
                throw new Error(`Invalid channel: ${channel}`)
        }
    }
}
